import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import axios from 'axios';
import Loading from '../../../Loading';

const formatPurchaserNames = (purchasers: Array<{name: string}>) => {
  if (!purchasers || purchasers.length === 0) return '[Purchaser Name]';
  
  const names = purchasers.map(p => p.name);
  
  if (names.length === 1) {
    return names[0];
  } else if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  } else {
    // For more than 2 names: "John, Jane, and Bob"
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  }
};

const JuratDetailModal = ({ purchasers, show, isRemoteSigning, onHide, lawyerName, JuratPackageList, msgId, sessionId, appId, province, meetingid = null, onStartEsigning, fileName, shortcode}) => {
  const [juratOption, setJuratOption] = useState<'same' | 'different'>('same');
  const [lawyerCityTownName, setLawyerCityTownName] = useState('');
  const [lawyerMunicipality, setLawyerMunicipality] = useState('');
  const [clientCityTownName, setClientCityTownName] = useState('');
  const [clientMunicipality, setClientMunicipality] = useState('');
  const [recordSession, setRecordSession] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const today = new Date();
  const month = today.toLocaleString('default', { month: 'long' });
  const day = today.getDate();
  const year = today.getFullYear();

  function getOrdinalSuffix(day: number): string {
    if (day > 3 && day < 21) return 'th'; // Covers 11th to 19th
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
  }

  const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;
  const swornText = purchasers && purchasers.length > 1 ? 'SEVERALLY Sworn' : 'Sworn';
  const juratPreview = !isRemoteSigning
    ? `${swornText} before me at the ${lawyerCityTownName || '[City/Town Name]'} in the ${lawyerMunicipality || '[Municipality]'} on this ${dayWithSuffix} day of ${month}, ${year}.`
    : juratOption === 'same'
      ? `${swornText} remotely by ${formatPurchaserNames(purchasers)} at the ${lawyerCityTownName || '[City/Town Name]'} in the ${lawyerMunicipality || '[Municipality]'}, before me on ${month} ${dayWithSuffix}, ${year} in accordance with O.Reg. 431/20 Administering Oath or Declaration Remotely.`
      : `${swornText} remotely by ${formatPurchaserNames(purchasers)} at the ${clientCityTownName || '[City/Town Name - Clients]'} in the ${clientMunicipality || '[Municipality - Clients]'}, before me at the ${lawyerCityTownName || '[City/Town Name - Lawyer]'} in the ${lawyerMunicipality || '[Municipality - Lawyer]'}, on ${month} ${dayWithSuffix}, ${year} in accordance with O. Reg. 431/20 Administering Oath or Declaration Remotely.`;

  const handleStartSigning = async () => {
    setIsLoading(true);
    try {
      await axios.post(
        `/docsigning/info/remotesigning/fields/${msgId}?SessionID=${sessionId}`,
        {
          packageList: JuratPackageList,
          juratText: '<P>' + juratPreview + '</P>',
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (isRemoteSigning) {
        onHide();
        await onStartEsigning(msgId, null, true);
        const currentUrl = window.location.pathname;
        const drParam = currentUrl.includes('/dr/') ? '&dr=true' : '';
        window.open('/' + process.env.REACT_APP_CLIENT_PORTAL_FRONTEND + '/joinRSLawyer' +
              '?SessionID=' + sessionId +
              '&province=' + province +
              '&appId=' + appId +
              '&meetingid=' + meetingid +
              '&shortcode=' + shortcode +
               drParam) 

      } else {
        onHide();
        await onStartEsigning(msgId, fileName, false);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Validation function to check if all required fields are filled
  const isFormValid = () => {
    if (!isRemoteSigning) {
      return lawyerCityTownName && lawyerMunicipality;
    } else if (juratOption === 'different') {
      return lawyerCityTownName && lawyerMunicipality && clientCityTownName && clientMunicipality;
    }
    return lawyerCityTownName && lawyerMunicipality;
  };

  return (
    <>
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>Session Preferences</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
            <Row>
                <Col>
                    {isRemoteSigning && (
                        <>
                            <Modal.Title>Jurat Details</Modal.Title>
                            <Form.Group>
                                <Form.Check
                                    type="radio"
                                    label="Deponent and commissioner are in the same city or town."
                                    name="juratOption"
                                    value="same"
                                    checked={juratOption === 'same'}
                                    onChange={() => setJuratOption('same')}
                                />
                                <Form.Check
                                    type="radio"
                                    label="Deponent and commissioner are not in the same city or town"
                                    name="juratOption"
                                    value="different"
                                    checked={juratOption === 'different'}
                                    onChange={() => setJuratOption('different')}
                                />
                            </Form.Group>
                        </>
                    )}

                    {!isRemoteSigning && (
                        <>
                        <Modal.Title>Jurat Details</Modal.Title>
                        <Row>
                            <Col>
                            <Form.Group>
                                <Form.Label>City/Town Name</Form.Label>
                                <Form.Control
                                type="text"
                                placeholder="e.g., City of Brampton"
                                value={lawyerCityTownName}
                                onChange={(e) => setLawyerCityTownName(e.target.value)}
                                />
                            </Form.Group>
                            </Col>
                            <Col>
                            <Form.Group>
                                <Form.Label>Municipality</Form.Label>
                                <Form.Control
                                type="text"
                                placeholder="e.g., Regional Municipality of Peel"
                                value={lawyerMunicipality}
                                onChange={(e) => setLawyerMunicipality(e.target.value)}
                                />
                            </Form.Group>
                            </Col>
                        </Row>
                        </>
                    )}

                    {isRemoteSigning && (
                        <>
                        {juratOption === 'different' && (
                            <h3 className="mt-3">Commissioner Location</h3>
                        )}
                        <Row>
                            <Col>
                                <Form.Group>
                                <Form.Label>{juratOption === 'different' ? 'Commissioner City/Town Name' : 'City/Town Name'}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g., City of Brampton"
                                    value={lawyerCityTownName}
                                    onChange={(e) => setLawyerCityTownName(e.target.value)}
                                />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group>
                                <Form.Label>{juratOption === 'different' ? 'Commissioner Municipality' : 'Municipality'}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g., Regional Municipality of Peel"
                                    value={lawyerMunicipality}
                                    onChange={(e) => setLawyerMunicipality(e.target.value)}
                                />
                                </Form.Group>
                            </Col>
                        </Row>
                        </>
                    )}

                    {isRemoteSigning && juratOption === 'different' && (
                        <>
                            <h3 className="mt-3">Client Location</h3>
                            <Row>
                                <Col>
                                    <Form.Group>
                                    <Form.Label>Client City/Town Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g., City of Brampton"
                                        value={clientCityTownName}
                                        onChange={(e) => setClientCityTownName(e.target.value)}
                                    />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group>
                                    <Form.Label>Client Municipality</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g., Regional Municipality of Peel"
                                        value={clientMunicipality}
                                        onChange={(e) => setClientMunicipality(e.target.value)}
                                    />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    )}

                    <Modal.Title className="mt-3">Jurat Preview</Modal.Title>
                    
                    <Form.Group>
                        <Form.Control as="textarea" rows={5} readOnly value={juratPreview} />
                    </Form.Group>
                </Col>
                {isRemoteSigning && false && (
                    <Col sm={5}>
                        <>
                            <Modal.Title>Session Recording</Modal.Title>
                            <Form.Group>
                            <p>You will be able to download the session recording at the end of your session. The session recording will be available for download for one week following the completion of the session.</p>
                            <Form.Check
                                type="checkbox"
                                label="Record Session"
                                checked={recordSession}
                                onChange={(e) => setRecordSession(e.target.checked)}
                            />
                            </Form.Group>
                        </>
                    </Col>
                )}
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-primary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={handleStartSigning} disabled={!isFormValid()}>
          Start Signing
        </Button>
      </Modal.Footer>
    </Modal>
    {isLoading && <Loading showMsg={true} extraMsg={isRemoteSigning ? "Starting Remote eSigning Session..." : "Starting eSigning Session..."} />}
    </>
  );
};

export default JuratDetailModal;